/**
 * AJAX Cart for SnifSnax
 * Modern, fun cart functionality with dog-themed animations
 */

class AjaxCart {
  constructor() {
    this.cart = null
    this.isOpen = false
    this.isUpdating = false

    // DOM Elements
    this.overlay = document.querySelector('[data-ajax-cart-overlay]')
    this.cartElement = document.querySelector('[data-ajax-cart]')
    this.trigger = document.querySelector('[data-ajax-cart-trigger]')
    this.closeButtons = document.querySelectorAll('[data-ajax-cart-close]')
    this.checkoutButton = document.querySelector('[data-cart-checkout]')

    // Initialize
    this.init()
  }

  init() {
    if (!this.overlay || !this.cartElement) {
      console.warn('AJAX Cart: Required elements not found')
      return
    }

    this.bindEvents()
    this.fetchCart()
  }

  bindEvents() {
    // // Cart trigger
    // if (this.trigger) {
    //   this.trigger.addEventListener('click', (e) => {
    //     e.preventDefault()
    //     this.openCart()
    //   })
    // }
    // Cart triggers (desktop + mobile + sticky clones)
    document.addEventListener(
      'click',
      (e) => {
        const trigger = e.target.closest('[data-ajax-cart-trigger]')
        if (!trigger) return

        // beat theme delegates
        e.preventDefault()
        if (e.stopImmediatePropagation) e.stopImmediatePropagation()
        e.stopPropagation()

        this.openCart()
        this.playAddToCartAnimation(trigger)
      },
      true // capture phase
    )

    // Close buttons
    this.closeButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault()
        this.closeCart()
      })
    })

    // Overlay click to close
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.closeCart()
      }
    })

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.closeCart()
      }
    })

    // Checkout button
    if (this.checkoutButton) {
      this.checkoutButton.addEventListener('click', () => {
        this.goToCheckout()
      })
    }

    // Quantity controls
    this.bindQuantityControls()

    // Remove buttons
    this.bindRemoveButtons()

    // Add to cart forms (global)
    this.bindAddToCartForms()

    // Subscribe & Save toggle
    this.bindSubscribeToggle()
  }

  bindQuantityControls() {
    // Plus buttons
    document.addEventListener('click', (e) => {
      if (e.target.matches('[data-quantity-plus]') || e.target.closest('[data-quantity-plus]')) {
        e.preventDefault()
        const button = e.target.matches('[data-quantity-plus]') ? e.target : e.target.closest('[data-quantity-plus]')
        const lineId = button.dataset.lineId
        const cartItem = button.closest('[data-cart-item]')
        const qtyDisplay = cartItem.querySelector('.qty-display')

        if (qtyDisplay) {
          const currentQty = parseInt(qtyDisplay.textContent) || 1
          const newQty = currentQty + 1
          this.updateQuantity(lineId, newQty)
        }
      }
    })

    // Minus buttons
    document.addEventListener('click', (e) => {
      if (e.target.matches('[data-quantity-minus]') || e.target.closest('[data-quantity-minus]')) {
        e.preventDefault()
        const button = e.target.matches('[data-quantity-minus]') ? e.target : e.target.closest('[data-quantity-minus]')
        const lineId = button.dataset.lineId
        const cartItem = button.closest('[data-cart-item]')
        const qtyDisplay = cartItem.querySelector('.qty-display')

        if (qtyDisplay) {
          const currentQty = parseInt(qtyDisplay.textContent) || 1
          const newQty = Math.max(1, currentQty - 1)
          this.updateQuantity(lineId, newQty)
        }
      }
    })

    // Input change (keeping for compatibility, though we're not using inputs now)
    document.addEventListener('change', (e) => {
      if (e.target.matches('[data-quantity-input]')) {
        const lineId = e.target.dataset.lineId
        const newQty = Math.max(0, parseInt(e.target.value) || 0)
        this.updateQuantity(lineId, newQty)
      }
    })
  }

  bindRemoveButtons() {
    document.addEventListener('click', (e) => {
      if (e.target.matches('[data-cart-remove]') || e.target.closest('[data-cart-remove]')) {
        e.preventDefault()
        const button = e.target.matches('[data-cart-remove]') ? e.target : e.target.closest('[data-cart-remove]')
        const lineId = button.dataset.lineId
        const variantId = button.dataset.variantId
        console.log('remove ', variantId)
        // Remove item without confirmation for better UX
        this.updateQuantity(lineId, 0)
      }
    })
  }

  bindAddToCartForms() {
    // Use capture phase and once flag to prevent duplicate bindings
    if (this.formHandlerBound) return
    this.formHandlerBound = true

    document.addEventListener(
      'submit',
      (e) => {
        if (e.target.matches('[action*="/cart/add"]') || e.target.querySelector('[name="add"]')) {
          // Skip if this form is being handled by subscription modal
          if (e.target.hasAttribute('data-subscription-processing')) {
            console.log('Skipping - handled by subscription modal')
            return
          }

          // Prevent other handlers from firing
          e.preventDefault()
          e.stopImmediatePropagation()

          this.addToCart(e.target)
        }
      },
      { capture: true }
    )
  }

  bindSubscribeToggle() {
    document.addEventListener('click', (e) => {
      if (e.target.matches('[data-subscribe-toggle]') || e.target.closest('[data-subscribe-toggle]')) {
        e.preventDefault()
        const button = e.target.matches('[data-subscribe-toggle]')
          ? e.target
          : e.target.closest('[data-subscribe-toggle]')
        const lineId = button.dataset.lineId
        const variantId = button.dataset.variantId
        const productHandle = button.dataset.productHandle

        this.switchToSubscription(lineId, variantId, productHandle)
      }
    })
  }

  async switchToSubscription(lineId, variantId, productHandle) {
    if (this.isUpdating) return

    const itemElement = document.querySelector(`[data-line-id="${lineId}"]`)
    if (itemElement) {
      this.setItemLoading(itemElement, true)
    }

    try {
      // First, get the current item details
      const currentItem = this.cart.items.find((item) => item.id == variantId)
      if (!currentItem) {
        throw new Error('Item not found in cart')
      }

      // Find subscription variant for this product
      const subscriptionData = await this.findSubscriptionVariant(productHandle, variantId)
      if (!subscriptionData) {
        throw new Error('No subscription option available for this product')
      }

      // Remove the current one-time purchase item
      await this.updateQuantity(lineId, 0)

      // Add the subscription variant with the same quantity
      await this.addSubscriptionToCart(subscriptionData.variantId, currentItem.quantity, subscriptionData.sellingPlanId)

      this.showSuccessMessage("Switched to subscription! You'll save 15% 📦")
    } catch (error) {
      console.error('Error switching to subscription:', error)
      this.showErrorMessage(error.message || 'Could not switch to subscription')
    } finally {
      if (itemElement) {
        this.setItemLoading(itemElement, false)
      }
    }
  }

  async findSubscriptionVariant(productHandle, currentVariantId) {
    try {
      // Fetch product data using the handle
      const response = await fetch(`/products/${productHandle}.js`)

      if (!response.ok) {
        console.log('Product not found:', productHandle)
        return null
      }

      const product = await response.json()

      // Get current variant details
      const currentVariant = product.variants.find((v) => v.id == currentVariantId)

      if (!currentVariant) {
        console.log('Current variant not found in product data')
        return null
      }

      // Check if the current variant has selling plans available
      if (currentVariant.selling_plan_allocations && currentVariant.selling_plan_allocations.length > 0) {
        // console.log('Current variant has subscription options:', currentVariant.id)
        const sellingPlanId = currentVariant.selling_plan_allocations[0].selling_plan_id
        return {
          variantId: currentVariant.id,
          sellingPlanId: sellingPlanId,
        }
      }

      // If current variant doesn't have selling plans, look for another variant with same options
      const subscriptionVariant = product.variants.find((variant) => {
        // Skip the current variant
        if (variant.id == currentVariantId) return false

        // Check if variant has selling plans available
        if (!variant.selling_plan_allocations || variant.selling_plan_allocations.length === 0) {
          return false
        }

        // Match variants with same option values
        return (
          variant.option1 === currentVariant.option1 &&
          variant.option2 === currentVariant.option2 &&
          variant.option3 === currentVariant.option3
        )
      })

      if (subscriptionVariant) {
        console.log('Found subscription variant:', subscriptionVariant.id)
        const sellingPlanId = subscriptionVariant.selling_plan_allocations[0].selling_plan_id
        return {
          variantId: subscriptionVariant.id,
          sellingPlanId: sellingPlanId,
        }
      } else {
        console.log('No subscription variant found for this product')
        return null
      }
    } catch (error) {
      console.error('Error finding subscription variant:', error)
      return null
    }
  }

  async addSubscriptionToCart(variantId, quantity, sellingPlanId) {
    try {
      const formData = new FormData()
      formData.append('id', variantId)
      formData.append('quantity', quantity)
      formData.append('selling_plan', sellingPlanId)

      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
      })

      if (!addResponse.ok) {
        const error = await addResponse.json()
        throw new Error(error.message || 'Failed to add subscription to cart')
      }

      await this.fetchCart()
    } catch (error) {
      console.error('Error adding subscription to cart:', error)
      throw error
    }
  }

  async fetchCart() {
    try {
      const response = await fetch('/cart.js')
      this.cart = await response.json()

      this.updateCartUI()
    } catch (error) {
      console.error('Error fetching cart:', error)
    }
  }

  async addToCart(form) {
    if (this.isUpdating) return

    this.setLoading(true)

    try {
      const formData = new FormData(form)
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
      })
      // console.log('formData', formData)
      if (response.ok) {
        await this.fetchCart()
        this.openCart()
        this.showSuccessMessage('Treat added to your pack! 🎉')
        this.playAddToCartAnimation()
      } else {
        const error = await response.json()
        this.showErrorMessage(error.message || 'Could not add item to cart')
      }
    } catch (error) {
      console.error('Error adding to cart:', error)
      this.showErrorMessage('Something went wrong. Please try again.')
    } finally {
      this.setLoading(false)
    }
  }

  async updateQuantity(lineId, quantity) {
    if (this.isUpdating) return

    const itemElement = document.querySelector(`[data-line-id="${lineId}"]`)
    if (itemElement) {
      this.setItemLoading(itemElement, true)
    }

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: lineId,
          quantity: quantity,
        }),
      })

      if (response.ok) {
        this.cart = await response.json()
        this.updateCartUI()

        if (quantity === 0) {
          this.showSuccessMessage('Item removed from pack 🗑️')
        }
      } else {
        throw new Error('Failed to update cart')
      }
    } catch (error) {
      console.error('Error updating cart:', error)
      this.showErrorMessage('Could not update quantity')
    } finally {
      if (itemElement) {
        this.setItemLoading(itemElement, false)
      }
    }
  }

  updateCartUI() {
    this.updateCartCount()
    this.updateCartItems()
    this.updateCartTotals()
    this.updateShippingProgress()
  }

  updateCartCount() {
    const countElements = document.querySelectorAll('[data-cart-count], [data-cart-icon-count]')
    countElements.forEach((element) => {
      const oldCount = element.textContent
      const newCount = this.cart.item_count

      element.textContent = newCount
      element.setAttribute('data-count', newCount)

      // Add animation if count changed
      if (oldCount !== newCount.toString()) {
        element.classList.add('updating')
        setTimeout(() => element.classList.remove('updating'), 300)
      }
    })
  }

  updateCartItems() {
    const itemsContainer = document.querySelector('[data-cart-items]')
    const emptyState = document.querySelector('[data-empty-cart]')
    const cartSummary = document.querySelector('.ajax-cart__summary')

    if (!itemsContainer) {
      console.error('updateCartItems: Items container not found!')
      return
    }

    if (this.cart.item_count === 0) {
      // Show empty state
      if (emptyState) {
        emptyState.style.display = 'block'
      }
      // Hide items container and summary
      itemsContainer.style.display = 'none'
      if (cartSummary) {
        cartSummary.style.display = 'none'
      }

      // Clear any remaining items from the container
      itemsContainer.innerHTML = ''
    } else {
      // Hide empty state
      if (emptyState) {
        emptyState.style.display = 'none'
      }
      // Show items container and summary
      itemsContainer.style.display = 'block'
      if (cartSummary) {
        cartSummary.style.display = 'block'
      }

      // Re-render cart items with proper HTML generation
      this.renderCartItems()
    }
  }

  renderCartItems() {
    const itemsContainer = document.querySelector('[data-cart-items]')
    if (!itemsContainer || !this.cart) {
      console.error('renderCartItems: Missing container or cart data', { itemsContainer, cart: this.cart })
      return
    }

    // console.log('renderCartItems: Rendering', this.cart.items.length, 'items')
    // console.log('Cart items:', this.cart.items)

    // Clear existing items
    itemsContainer.innerHTML = ''

    // Generate HTML for each cart item
    this.cart.items.forEach((item, index) => {
      const itemHTML = this.generateCartItemHTML(item, index + 1)
      itemsContainer.insertAdjacentHTML('beforeend', itemHTML)
    })
  }

  generateCartItemHTML(item, lineId) {
    // Add safety checks for item structure
    if (!item) {
      console.error('Cart item is undefined:', item)
      return ''
    }

    // In Shopify Cart API, the product info is directly on the item, not nested under 'product'
    const hasVariants = item.has_only_default_variant === false
    const hasProperties = item.properties && Object.keys(item.properties).length > 0
    const hasSellingPlan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
    const excludeFromSubSave = item.product_type == 'Single'

    // Generate options HTML
    let optionsHTML = ''
    if (hasVariants || hasProperties || hasSellingPlan) {
      optionsHTML = '<div class="ajax-cart-item__options">'

      // Variant options
      if (hasVariants && item.options_with_values) {
        item.options_with_values.forEach((option) => {
          optionsHTML += `<span class="ajax-cart-item__option">${option.name}: ${option.value}</span>`
        })
      }

      // Properties
      if (hasProperties) {
        Object.entries(item.properties).forEach(([key, value]) => {
          if (value && !key.startsWith('_')) {
            optionsHTML += `<span class="ajax-cart-item__option">${key}: ${value}</span>`
          }
        })
      }

      // Selling plan
      if (hasSellingPlan) {
        optionsHTML += `<span class="ajax-cart-item__option subscription-plan">📦 ${item.selling_plan_allocation.selling_plan.name}</span>`
      }

      optionsHTML += '</div>'
    }

    // Generate Subscribe & Save toggle for one-time purchases
    // Only show if this item has a subscription option available
    let subscribeToggleHTML = ''
    if (!hasSellingPlan && item.handle && !excludeFromSubSave) {
      subscribeToggleHTML = `
        <div class="cart-item__subscribe-toggle">
          <button
            type="button"
            class="subscribe-toggle-btn"
            data-subscribe-toggle
            data-line-id="${lineId}"
            data-variant-id="${item.id}"
            data-product-handle="${item.handle}"
            title="Switch to subscription and save"
          >
            <span class="subscribe-icon">📦</span>
            <span class="subscribe-text">Subscribe & Save</span>
            <span class="subscribe-savings">Save 15%</span>
          </button>
        </div>
      `
    }

    // Generate price HTML
    let priceHTML = ''
    if (item.original_price !== item.final_price) {
      priceHTML = `
        <span class="ajax-cart-item__price--sale money">${this.formatMoney(item.final_price)}</span>
        <span class="ajax-cart-item__price--original money">${this.formatMoney(item.original_price)}</span>
      `
    } else {
      priceHTML = `<span class="ajax-cart-item__price--regular money">${this.formatMoney(item.original_price)}</span>`
    }

    // Unit price
    let unitPriceHTML = ''
    if (item.unit_price_measurement) {
      const referenceValue =
        item.unit_price_measurement.reference_value !== 1 ? item.unit_price_measurement.reference_value : ''
      unitPriceHTML = `
        <div class="ajax-cart-item__unit-price">
          <span class="money">${this.formatMoney(item.unit_price)}</span>
          <span class="unit-price-separator">/</span>
          <span class="unit-price-unit">${referenceValue}${item.unit_price_measurement.reference_unit}</span>
        </div>
      `
    }

    // Generate image HTML
    let imageHTML = ''
    if (item.image) {
      imageHTML = `<img src="${item.image}" alt="${
        item.product_title || item.title
      }" style="width: 80px; height: 80px; object-fit: cover; border-radius: 8px;">`
    } else {
      imageHTML = `
        <div class="ajax-cart-item__no-image">
          <span class="no-image-icon">🦴</span>
        </div>
      `
    }

    return `
      <div class="cart-item" data-cart-item data-line-id="${lineId}" data-variant-id="${item.id}">
        <div class="cart-item__content">
          <!-- Product Image -->
          <div class="cart-item__image">
            <a href="${item.url}">
              ${imageHTML}
            </a>
          </div>

          <!-- Product Details -->
          <div class="cart-item__details">
            <div class="cart-item__header">
              <h4 class="cart-item__title">
                <a href="${item.url}">${item.product_title || item.title}</a>
              </h4>
              <!-- Remove Button -->
              <button
                type="button"
                class="cart-item__remove"
                data-cart-remove
                data-line-id="${lineId}"
                aria-label="Remove ${item.product_title || item.title} from cart"
                title="Remove item"
              >
                ×
              </button>
            </div>

            ${optionsHTML}

            ${subscribeToggleHTML}

            <!-- Price and Quantity Row -->
            <div class="cart-item__footer">
              <div class="cart-item__price">
                ${priceHTML}
                ${unitPriceHTML}
              </div>

              <!-- Quantity Controls -->
              <div class="cart-item__quantity">
                <button
                  type="button"
                  class="qty-btn qty-minus"
                  data-quantity-minus
                  data-line-id="${lineId}"
                  aria-label="Decrease quantity"
                  ${item.quantity <= 1 ? 'disabled' : ''}
                >
                  −
                </button>

                <span class="qty-display">${item.quantity}</span>

                <button
                  type="button"
                  class="qty-btn qty-plus"
                  data-quantity-plus
                  data-line-id="${lineId}"
                  aria-label="Increase quantity"
                >
                  +
                </button>
              </div>

              <!-- Line Total -->
              <div class="cart-item__total">
                <span class="money" data-line-total>${this.formatMoney(item.final_line_price)}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Loading State for Individual Item -->
        <div class="cart-item__loading" data-item-loading style="display: none;">
          <div class="loading-spinner">🐾</div>
        </div>
      </div>
    `
  }

  updateCartTotals() {
    const totalElements = document.querySelectorAll('[data-cart-total], [data-cart-total-checkout]')
    totalElements.forEach((element) => {
      element.textContent = this.formatMoney(this.cart.total_price)
    })
  }

  updateShippingProgress() {
    const progressBar = document.querySelector('.shipping-progress__fill')
    const progressText = document.querySelector('.shipping-progress__text')
    const freeShippingMessage = document.querySelector('.ajax-cart__free-shipping')
    const shippingProgress = document.querySelector('.ajax-cart__shipping-progress')

    if (!progressBar || !progressText) return

    const threshold = 4999
    const cartTotal = this.cart.total_price
    const remaining = threshold - cartTotal

    if (remaining > 0) {
      const progress = (cartTotal / threshold) * 100
      progressBar.style.width = `${Math.min(progress, 100)}%`

      const remainingFormatted = this.formatMoney(remaining)
      progressText.innerHTML = `<span class="shipping-progress__icon">🚚</span>Add <strong class="money">${remainingFormatted}</strong> more for FREE shipping!`

      if (shippingProgress) shippingProgress.style.display = 'block'
      if (freeShippingMessage) freeShippingMessage.style.display = 'none'
    } else {
      if (shippingProgress) shippingProgress.style.display = 'none'
      if (freeShippingMessage) freeShippingMessage.style.display = 'block'
    }
  }

  openCart() {
    this.isOpen = true
    this.overlay.classList.add('is-open')
    document.body.style.overflow = 'hidden'

    // Focus management for accessibility
    this.cartElement.focus()
  }

  closeCart() {
    this.isOpen = false
    this.overlay.classList.remove('is-open')
    document.body.style.overflow = ''
  }

  goToCheckout() {
    window.location.href = '/checkout'
  }

  setLoading(loading) {
    this.isUpdating = loading
    const loadingElement = document.querySelector('[data-cart-loading]')
    if (loadingElement) {
      loadingElement.style.display = loading ? 'flex' : 'none'
    }
  }

  setItemLoading(itemElement, loading) {
    const loadingElement = itemElement.querySelector('[data-item-loading]')
    if (loadingElement) {
      loadingElement.style.display = loading ? 'flex' : 'none'
    }
  }

  showSuccessMessage(message) {
    // this.showNotification(message, 'success')
  }

  showErrorMessage(message) {
    // this.showNotification(message, 'error')
  }

  showNotification(message, type = 'info') {
    // Create notification element
    const notification = document.createElement('div')
    notification.className = `ajax-cart-notification ajax-cart-notification--${type}`
    notification.innerHTML = `
      <div class="ajax-cart-notification__content">
        <span class="ajax-cart-notification__icon">
          ${type === 'success' ? '✅' : type === 'error' ? '❌' : 'ℹ️'}
        </span>
        <span class="ajax-cart-notification__message">${message}</span>
      </div>
    `

    // Add styles
    notification.style.cssText = `
      position: fixed;
      bottom: 60px;
      right: 20px;
      background: ${type === 'success' ? '#12a0db' : type === 'error' ? '#f8d7da' : '#12a0db'};
      color: ${type === 'success' ? '#fff' : type === 'error' ? '#721c24' : '#fff'};
      padding: 1rem 1.5rem;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      transform: translateX(100%);
      transition: transform 0.3s ease;
      max-width: 300px;
      font-size: 0.875rem;
      font-weight: 500;
    `

    document.body.appendChild(notification)

    // Animate in
    requestAnimationFrame(() => {
      notification.style.transform = 'translateX(0)'
    })

    // Remove after delay
    setTimeout(() => {
      notification.style.transform = 'translateX(100%)'
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification)
        }
      }, 300)
    }, 3000)
  }

  // playAddToCartAnimation() {
  //   // Fun dog-themed animation when item is added
  //   const cartIcon = document.querySelector('[data-ajax-cart-trigger]')
  //   if (cartIcon) {
  //     cartIcon.style.animation = 'none'
  //     requestAnimationFrame(() => {
  //       cartIcon.style.animation = 'wagTail 0.6s ease'
  //     })
  //   }
  // }

  playAddToCartAnimation(icon) {
    const icons = icon ? [icon] : document.querySelectorAll('[data-ajax-cart-trigger]')
    icons.forEach((el) => {
      el.style.animation = 'none'
      requestAnimationFrame(() => {
        el.style.animation = 'wagTail 0.6s ease'
      })
    })
  }

  formatMoney(cents) {
    return `$${(cents / 100).toFixed(2)}`
  }
}

// Additional CSS for animations and notifications
const additionalStyles = `
  <style>
    @keyframes wagTail {
      0%, 100% { transform: rotate(0deg); }
      25% { transform: rotate(-5deg); }
      75% { transform: rotate(5deg); }
    }

    .ajax-cart-notification__content {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .ajax-cart-notification__icon {
      flex-shrink: 0;
    }

    /* Fun loading animations */
    @keyframes pawWalk {
      0% { transform: translateX(-10px) rotate(-10deg); }
      50% { transform: translateX(10px) rotate(10deg); }
      100% { transform: translateX(-10px) rotate(-10deg); }
    }

    .loading-paw {
      animation: pawWalk 1s ease-in-out infinite;
    }

    /* Modern Cart Item Styles */
    .cart-item {
      padding: 1rem 0;
      border-bottom: 1px solid #f0f0f0;
      position: relative;
    }

    .cart-item:last-child {
      border-bottom: none;
    }

    .cart-item__content {
      display: flex;
      gap: 1rem;
      align-items: flex-start;
    }

    .cart-item__image {
      flex-shrink: 0;
      width: 80px;
      height: 80px;
      border-radius: 8px;
      overflow: hidden;
    }

    .cart-item__image img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .cart-item__details {
      flex: 1;
      min-width: 0;
    }

    .cart-item__header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 0.5rem;
    }

    .cart-item__title {
      font-size: 0.9rem;
      font-weight: 600;
      margin: 0;
      line-height: 1.3;
      flex: 1;
      margin-right: 0.5rem;
    }

    .cart-item__title a {
      color: #333;
      text-decoration: none;
    }

    .cart-item__title a:hover {
      color: #ff6b35;
    }

    .cart-item__remove {
      background: none;
      border: none;
      color: #999;
      cursor: pointer;
      font-size: 1.25rem;
      line-height: 1;
      padding: 0.25rem;
      border-radius: 50%;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
    }

    .cart-item__remove:hover {
      color: #dc3545;
      background: rgba(220, 53, 69, 0.1);
    }

    .ajax-cart-item__options {
      margin-bottom: 0.75rem;
    }

    .ajax-cart-item__option {
      display: block;
      font-size: 0.75rem;
      color: #666;
      line-height: 1.4;
    }

    .cart-item__footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 1rem;
    }

    .cart-item__price {
      display: none;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    .ajax-cart-item__price--sale {
      color: #dc3545;
      font-weight: 600;
    }

    .ajax-cart-item__price--original {
      color: #999;
      text-decoration: line-through;
      font-size: 0.875rem;
    }

    .ajax-cart-item__price--regular {
      font-weight: 600;
      color: #333;
    }

    .ajax-cart-item__unit-price {
      font-size: 0.75rem;
      color: #666;
      width: 100%;
    }

    /* Subscribe & Save Toggle Styles */
    .cart-item__subscribe-toggle {
      margin: 0.75rem 0;
    }

    .subscribe-toggle-btn {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      background: linear-gradient(135deg, #28a745, #20c997);
      color: white;
      border: none;
      border-radius: 6px;
      padding: 0.5rem 0.75rem;
      font-size: 0.8125rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
      width: 100%;
      justify-content: center;
      position: relative;
      overflow: hidden;
    }

    .subscribe-toggle-btn:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
      background: linear-gradient(135deg, #218838, #1e7e34);
    }

    .subscribe-toggle-btn:active {
      transform: translateY(0);
    }

    .subscribe-icon {
      font-size: 1rem;
    }

    .subscribe-text {
      flex: 1;
    }

    .subscribe-savings {
      background: rgba(255, 255, 255, 0.2);
      padding: 0.125rem 0.375rem;
      border-radius: 12px;
      font-size: 0.75rem;
      font-weight: 700;
    }

    .subscription-plan {
      color: #28a745 !important;
      font-weight: 600;
    }

    .cart-item__quantity {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      background: #f8f9fa;
      border-radius: 6px;
      // padding: 0.25rem;
    }

    .qty-btn {
      background: white;
      border: 1px solid #ddd;
      width: 28px;
      height: 28px;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      font-size: 0.875rem;
      font-weight: 600;
      color: #666;
      transition: all 0.2s ease;
    }

    .qty-btn:hover:not(:disabled) {
      background: #e9ecef;
      color: #333;
      border-color: #adb5bd;
    }

    .qty-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .qty-display {
      min-width: 24px;
      text-align: center;
      font-weight: 600;
      font-size: 0.875rem;
      color: #333;
    }

    .cart-item__total {
      font-weight: 600;
      color: #333;
      font-size: 0.9rem;
      text-align: right;
      min-width: 60px;
    }

    .cart-item__loading {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(255, 255, 255, 0.9);
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 8px;
    }

    .loading-spinner {
      font-size: 1.5rem;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    /* Mobile Responsiveness */
    @media (max-width: 480px) {
      .cart-item__content {
        gap: 0.75rem;
      }

      .cart-item__image {
        width: 60px;
        height: 60px;
      }

      .cart-item__title {
        font-size: 0.8125rem;
      }

      .cart-item__footer {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.75rem;
      }

      .qty-btn {
        width: 24px;
        height: 24px;
        font-size: 0.75rem;
      }

      .cart-item__total {
        align-self: flex-end;
      }
    }
  </style>
`

// Inject additional styles
document.head.insertAdjacentHTML('beforeend', additionalStyles)

// Initialize cart when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.ajaxCart = new AjaxCart()
  })
} else {
  window.ajaxCart = new AjaxCart()
}

// Export for potential external use
window.AjaxCart = AjaxCart
